import { CARRIER_CODES, type CarrierCode } from "@flexfreight/core";
import abf from "../../../assets/carriers/abf.png";
import amfast from "../../../assets/carriers/amfast.png";
import estes from "../../../assets/carriers/estes.png";
import fedexFreight from "../../../assets/carriers/fedex_freight.png";
import odfl from "../../../assets/carriers/odfl.png";
import rl from "../../../assets/carriers/rl.png";
import saia from "../../../assets/carriers/saia.png";
import xpo from "../../../assets/carriers/xpo.png";

/**
 * How each registry carrier is shown. `logo` uses the official file from assets/carriers as-is
 * (never redrawn, traced or recoloured); `badge` is the explicit neutral text fallback for a
 * carrier we have no official logo file for yet. `remote` is an official file the API serves from its own
 * `/v1/carrier-logos/` path (EagleLoad's logo for that carrier, or one staff uploaded), also shown as-is.
 */
export type BundledCarrierMark =
  | { kind: "logo"; file: string; src: string; /** Square source file (a seal), drawn edge-to-edge in the chip so it isn't a dot. */ square?: boolean }
  | { kind: "badge"; label: string };
export type CarrierMark = BundledCarrierMark | { kind: "remote"; src: string };

/** Typed as a full Record so adding a carrier code to the registry fails typecheck until it picks one. */
export const CARRIER_MARKS: Readonly<Record<CarrierCode, BundledCarrierMark>> = {
  xpo: { kind: "logo", file: "xpo.png", src: xpo },
  odfl: { kind: "logo", file: "odfl.png", src: odfl, square: true },
  estes: { kind: "logo", file: "estes.png", src: estes },
  saia: { kind: "logo", file: "saia.png", src: saia },
  rl: { kind: "logo", file: "rl.png", src: rl },
  abf: { kind: "logo", file: "abf.png", src: abf },
  fedex_freight: { kind: "logo", file: "fedex_freight.png", src: fedexFreight },
  amfast: { kind: "logo", file: "amfast.png", src: amfast },
  // No official file yet (QUESTIONS.md run 0.3).
  tforce: { kind: "badge", label: "TForce Freight" },
  // EagleLoad and WWEX rates name the real carrier; carrierMark() shows that carrier's mark when it is one we know.
  eagleload: { kind: "badge", label: "EagleLoad" },
  wwex: { kind: "badge", label: "Worldwide Express" },
  mock: { kind: "badge", label: "Mock carrier" },
};

const isCode = (c: string): c is CarrierCode => (CARRIER_CODES as readonly string[]).includes(c);
/** Aggregators whose options name the real carrier. */
const AGGREGATORS: ReadonlySet<string> = new Set(["eagleload", "wwex"]);

/** Carrier names as EagleLoad reports them → our codes, for showing the real carrier's logo on an EagleLoad rate. */
const NAMED: readonly [RegExp, CarrierCode][] = [
  [/\bxpo\b/i, "xpo"],
  [/old dominion|\bodfl\b/i, "odfl"],
  [/\bestes\b/i, "estes"],
  [/\bsaia\b/i, "saia"],
  [/\br\s*\+?\s*&?\s*l\b|\brl carriers\b/i, "rl"],
  [/\babf\b|arcbest/i, "abf"],
  [/fedex/i, "fedex_freight"],
  [/tforce/i, "tforce"],
];

/** Only the API's own logo path is ever rendered (same origin, so CSP `img-src 'self'` holds); anything else is ignored. */
export const ownLogoUrl = (u: string | null | undefined): string | null =>
  typeof u === "string" && /^\/v1\/carrier-logos\/[a-f0-9]{64}\.(png|jpg|webp)$/.test(u) ? u : null;

/**
 * Mark for a carrier code: a supplied `logoUrl` (the API's own path) first, then the bundled official file, then a
 * text badge with the given fallback name. EagleLoad options carry the real carrier's name and, when EagleLoad's
 * carrier is one we also know, its code (`operatedBy`, from packages/carriers eagleload mapping): a carrier we know
 * gets its own logo, any other a text badge with its name.
 */
export const carrierMark = (code: string, fallbackName: string, operatedBy?: string | null, logoUrl?: string | null): CarrierMark => {
  const own = ownLogoUrl(logoUrl);
  if (own) return { kind: "remote", src: own };
  if (AGGREGATORS.has(code)) {
    if (operatedBy && isCode(operatedBy) && !AGGREGATORS.has(operatedBy) && operatedBy !== "mock") return CARRIER_MARKS[operatedBy];
    const hit = NAMED.find(([re]) => re.test(fallbackName));
    return hit ? CARRIER_MARKS[hit[1]] : { kind: "badge", label: fallbackName || (code === "wwex" ? "Worldwide Express" : "EagleLoad") };
  }
  return isCode(code) ? CARRIER_MARKS[code] : { kind: "badge", label: fallbackName };
};

const NOISE = /^(the|inc|llc|co|corp|company|ltd)$/i;
/** "Frontline Freight" → "FF", "Southeastern Freight Lines" → "SF", "Pitt Ohio" → "PO", "Dayton" → "DA". */
export function carrierMonogram(name: string): string {
  const words = name.replace(/[^\p{L}\p{N}\s]+/gu, " ").split(/\s+/).filter((w) => w && !NOISE.test(w));
  if (words.length === 0) return "?";
  if (words.length === 1) return words[0]!.slice(0, 2).toUpperCase();
  return (words[0]![0]! + words[1]![0]!).toUpperCase();
}

export type CarrierLogoProps = {
  /** Registry carrier code (xpo, odfl, …). */
  code: string;
  /** Display name; used as alt text and shown beside the logo. */
  name: string;
  /** EagleLoad rates: our code for the carrier that actually runs the freight, when it is one we know. */
  operatedBy?: string | null;
  /** The carrier's official logo served by the API (`/v1/carrier-logos/...`); wins over the bundled file. */
  logoUrl?: string | null;
  /** Hide the visible name when it is already shown right next to the chip (e.g. a heading). */
  showName?: boolean;
  /**
   * The carrier's name is already visible text right beside the logo (a Select option or trigger, a labelled
   * row): the image is then decorative (empty alt) so screen readers don't hear the name twice.
   */
  decorative?: boolean;
  size?: "sm" | "md" | "lg";
  /** Carriers without a logo file: the name as a text badge (portal default) or a two-letter monogram tile. */
  fallback?: "badge" | "monogram";
  className?: string;
};

/**
 * Official carrier logo on a white rounded chip, with the carrier name in text beside it.
 * Carriers without an official logo file get a neutral text badge (or a monogram tile) instead, never a drawn logo.
 * Needs @flexfreight/ui/carrier.css.
 */
export function CarrierLogo({ code, name, operatedBy, logoUrl, showName = true, decorative = false, size = "md", fallback = "badge", className }: CarrierLogoProps) {
  const mark = carrierMark(code, name, operatedBy, logoUrl);
  const cls = ["ff-carrier", size === "md" ? "" : `ff-carrier--${size}`, className ?? ""].filter(Boolean).join(" ");
  if (mark.kind === "badge" && fallback === "monogram") {
    return (
      <span className={cls} data-carrier={code}>
        <span
          className="ff-carrier__chip ff-carrier__chip--mono"
          {...(decorative ? { "aria-hidden": true } : { role: "img", "aria-label": name })}
        >
          {carrierMonogram(name)}
        </span>
        {showName ? <span className="ff-carrier__name" aria-hidden="true">{name}</span> : null}
      </span>
    );
  }
  if (mark.kind === "badge") {
    // The badge already is the name in text, so it is never repeated beside itself.
    return (
      <span className={cls} data-carrier={code}>
        {/* Decorative: the name is announced by the surrounding label, so the badge text isn't read twice. */}
        <span className="ff-carrier__chip ff-carrier__chip--badge" aria-hidden={decorative || undefined}>{mark.label}</span>
      </span>
    );
  }
  if (mark.kind === "remote") {
    return (
      <span className={cls} data-carrier={code}>
        {/* Fixed 3:1 chip box (carrier.css), so the lazy image never shifts the layout; the file is contain-fit as-is. */}
        <span className="ff-carrier__chip ff-carrier__chip--remote">
          <img src={mark.src} alt={decorative ? "" : name} loading="lazy" decoding="async" />
        </span>
        {showName ? <span className="ff-carrier__name" aria-hidden="true">{name}</span> : null}
      </span>
    );
  }
  return (
    <span className={cls} data-carrier={code}>
      <span className={mark.square ? "ff-carrier__chip ff-carrier__chip--square" : "ff-carrier__chip"}>
        {/* Eager on purpose (P.1.3): there are only 8 small files, the browser fetches each URL once, and
            lazy chips painted as blank white boxes until scrolled into view (and in every full-page capture). */}
        <img src={mark.src} alt={decorative ? "" : name} loading="eager" decoding="async" />
      </span>
      {/* The img alt already names the carrier for screen readers; the text is for sighted users. */}
      {showName ? <span className="ff-carrier__name" aria-hidden="true">{name}</span> : null}
    </span>
  );
}

/** True when the chip already shows the carrier's name as text (a badge), so a row needn't repeat it visibly. */
export const chipShowsName = (code: string, name: string, operatedBy?: string | null, fallback: "badge" | "monogram" = "badge", logoUrl?: string | null) =>
  fallback === "badge" && carrierMark(code, name, operatedBy, logoUrl).kind === "badge";
